import { useEffect } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import Nav from "@/components/Nav";
import CartDrawer from "@/components/CartDrawer";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Morphs from "@/components/Morphs";
import Products from "@/components/Products";
import Terrariums from "@/components/Terrariums";
import Enquiry from "@/components/Enquiry";
import Footer from "@/components/Footer";
import KassePage from "@/pages/KassePage";
import LinjeavlPage from "@/pages/LinjeavlPage";
import AdminPage from "@/pages/AdminPage";
import MinKontoPage from "@/pages/MinKontoPage";
import VarePage from "@/pages/VarePage";
import NulstilPage from "@/pages/NulstilPage";
import KontaktPage from "@/pages/KontaktPage";
import HandelsbetingelserPage from "@/pages/HandelsbetingelserPage";
import PlejeguidePage from "@/pages/PlejeguidePage";
import MessekalenderPage from "@/pages/MessekalenderPage";
import MesseBanner from "@/components/MesseBanner";
import { BetalingSuccess, BetalingCancel } from "@/pages/BetalingPage";
import { trackPage } from "@/lib/site";

const TITLES = {
  "/": "Køb Kronegekkoer — GEKKOFUEL",
  "/kasse": "Kassen — GEKKOFUEL",
  "/min-konto": "Min konto — GEKKOFUEL",
  "/linjeavl": "GEKKOFUEL Linjeavl — GEKKOFUEL",
  "/kontakt": "Kontakt — GEKKOFUEL",
  "/handelsbetingelser": "Handelsbetingelser — GEKKOFUEL",
  "/plejeguide": "Plejeguide: Correlophus ciliatus — GEKKOFUEL",
  "/messekalender": "Messekalender — GEKKOFUEL",
  "/admin": "Admin — GEKKOFUEL",
  "/nulstil-kodeord": "Nulstil adgangskode — GEKKOFUEL",
  "/betaling/gennemfoert": "Ordre gennemført — GEKKOFUEL",
  "/betaling/annulleret": "Betaling annulleret — GEKKOFUEL",
};

function RouteTitles() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = TITLES[pathname] || "GEKKOFUEL — Kronegekkoer, Morfer & Terrarier";
    if (!pathname.startsWith("/admin")) {
      trackPage(pathname);
    }
    // Genaktivér engelsk oversættelse på ny side (SPA-navigation renderer nyt dansk DOM)
    const isEn =
      document.cookie.includes("googtrans=/da/en") ||
      document.cookie.includes("googtrans=%2Fda%2Fen");
    if (isEn) {
      const reapply = () =>
        document.querySelector('.gtranslate_wrapper a[data-gt-lang="en"]')?.click();
      const timers = [400, 1800, 4000].map((ms) => setTimeout(reapply, ms));
      return () => timers.forEach(clearTimeout);
    }
  }, [pathname]);
  return null;
}

function Home() {
  return (
    <main>
      <div className="pt-24">
        <MesseBanner />
      </div>
      <Hero />
      <Marquee />
      <Morphs />
      <Products />
      <Terrariums />
      <Enquiry />
    </main>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <CartProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-ink text-cream font-sans">
          <Nav />
          <CartDrawer />
          <RouteTitles />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/kasse" element={<KassePage />} />
            <Route path="/linjeavl" element={<LinjeavlPage />} />
            <Route path="/fremtidsplaner" element={<Navigate to="/linjeavl" replace />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/min-konto" element={<MinKontoPage />} />
            <Route path="/vare/:slug" element={<VarePage />} />
            <Route path="/nulstil-kodeord" element={<NulstilPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/handelsbetingelser" element={<HandelsbetingelserPage />} />
            <Route path="/plejeguide" element={<PlejeguidePage />} />
            <Route path="/messekalender" element={<MessekalenderPage />} />
            <Route path="/betaling/gennemfoert" element={<BetalingSuccess />} />
            <Route path="/betaling/annulleret" element={<BetalingCancel />} />
          </Routes>
          <Footer />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#121411",
                border: "1px solid rgba(244,241,235,0.12)",
                color: "#F4F1EB",
              },
            }}
          />
        </div>
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
